import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, isRef as _isRef, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "reports-accounts-tab" }
const _hoisted_2 = { class: "reports-tab__toolbar" }
const _hoisted_3 = { class: "reports-tab__toolbar-right" }
const _hoisted_4 = { class: "reports-tab__stats" }
const _hoisted_5 = { class: "reports-accounts-tab__chart" }

import { computed } from 'vue';
import DateRangePicker from '@/components/ui/DateRangePicker.vue';
import ReportsStatCards from '@/components/reports/ReportsStatCards.vue';
import ToolbarIconButton from '@/components/ui/ToolbarIconButton.vue';
import IconExport from '@/components/icons/IconExport.vue';
import BarChart from '@/components/ui/BarChart.vue';
import { useReportsAccounts } from '@/composables/useReportsAccounts';
import { formatDate } from '@/utils/formatUtils';
import { downloadCsv } from '@/utils/csvExport';
import { useDateRangeWithTimezone } from '@/composables/useDateRangeWithTimezone';


export default /*@__PURE__*/_defineComponent({
  __name: 'ReportsAccountsTab',
  setup(__props) {

/* eslint-env vue/setup-compiler-macros */
const { selectedDateRange } = useDateRangeWithTimezone();

const formattedDateRange = computed<[string, string] | null>(() => {
    const range = selectedDateRange.value;
    if (!range) return null;
    return [formatDate(range[0]), formatDate(range[1])];
});

const {
    viewMode,
    activeStatId,
    stats,
    chartData,
    chartOptions,
    dailyAverage,
    loading,
    categoryData,
    rangeIncludesCurrentPeriod,
} = useReportsAccounts(formattedDateRange);

const CATEGORY_LABELS: Record<string, string> = {
    validated: 'Validated',
    'pending-validation': 'Pending Validation',
    delinquent: 'Delinquent',
    'unsettled-accounts': 'Unsettled Accounts',
};

function exportCsv() {
    const cats = categoryData.value;
    const activeId = activeStatId.value;
    const range = formattedDateRange.value;
    if (!range) return;
    const [from, to] = range;
    const dateRangeLabel = `${from} - ${to}`;
    const fileSlug = `${from.replace(/\//g, '-')}_${to.replace(/\//g, '-')}`;
    const fmtNum = (v: number) => v.toLocaleString('en-US');

    if (activeId && cats[activeId]) {
        const label = CATEGORY_LABELS[activeId] ?? activeId;
        const statItem = stats.value.find((s) => s.id === activeId);
        const rows: string[][] = [
            ['Date Range', dateRangeLabel],
            [],
            ['Period', label],
            ...cats[activeId].labels.map((lbl, i) => [lbl, fmtNum(cats[activeId].values[i])]),
            [],
            [`Total ${label}`, String(statItem?.value ?? '')],
            ['Daily Average', dailyAverage.value],
        ];
        downloadCsv(`accounts-${activeId}-${viewMode.value}-${fileSlug}.csv`, rows);
        return;
    }

    const catKeys = ['validated', 'pending-validation', 'delinquent', 'unsettled-accounts'] as const;
    const labels = cats.validated.labels;
    const header = ['Period', ...catKeys.map((k) => CATEGORY_LABELS[k]), 'Daily Total'];
    const dataRows: string[][] = labels.map((lbl, i) => {
        const dayTotal = catKeys.reduce((sum, k) => sum + (cats[k]?.values[i] ?? 0), 0);
        return [lbl, ...catKeys.map((k) => fmtNum(cats[k]?.values[i] ?? 0)), fmtNum(dayTotal)];
    });
    const summaryRows: string[][] = [
        [],
        ...stats.value.map((s) => [s.label, String(s.value)]),
        ['Daily Average', dailyAverage.value],
    ];
    downloadCsv(`accounts-${viewMode.value}-${fileSlug}.csv`, [['Date Range', dateRangeLabel], [], header, ...dataRows, ...summaryRows]);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(DateRangePicker, {
        modelValue: _unref(selectedDateRange),
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(selectedDateRange) ? (selectedDateRange).value = $event : null)),
        label: "Filter by Date:",
        "trigger-width": "350px",
        "trigger-radius": "10px"
      }, null, 8, ["modelValue"]),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(ToolbarIconButton, {
          ariaLabel: "Export",
          onClick: exportCsv
        }, {
          default: _withCtx(() => [
            _createVNode(IconExport)
          ]),
          _: 1
        })
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(ReportsStatCards, {
        items: _unref(stats),
        "active-id": _unref(activeStatId),
        loading: _unref(loading),
        "onUpdate:activeId": _cache[1] || (_cache[1] = ($event: any) => (activeStatId.value = $event))
      }, null, 8, ["items", "active-id", "loading"])
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(BarChart, {
        "chart-data": _unref(chartData),
        "daily-average": _unref(activeStatId) ? _unref(dailyAverage) : null,
        loading: _unref(loading),
        "highlight-current-period": _unref(rangeIncludesCurrentPeriod),
        "chart-options": _unref(chartOptions),
        "value-formatter": (val) => val.toLocaleString(),
        "onUpdate:viewMode": _cache[2] || (_cache[2] = ($event: any) => (viewMode.value = $event))
      }, null, 8, ["chart-data", "daily-average", "loading", "highlight-current-period", "chart-options", "value-formatter"])
    ])
  ]))
}
}

})